import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Divider,
  // Button,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import transformOWASPData from "../DASTInsightsPages/charts/transformOWASPData";
import ProgressBarMUI from "../DASTInsightsPages/charts/ProgressBarMUI";
import InfoIcon from "@mui/icons-material/Info";

// Function to get dynamic description based on score
const getSecurityPostureDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Posture";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Posture";
  } else if (score >= 7.0 && score <= 8.9) {
    return "HighMedium Posture";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Posture";
  } else {
    return "Unknown";
  }
};

// Function to get dynamic description based on business impact score
const getBusinessImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  } else {
    return "Unknown Impact";
  }
};

// Helper to turn a technical-impact score into a label
const getTechnicalImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  }
  return "Unknown Impact";
};

// Function to get dynamic description based on risk weighted score
const getRiskWeightedDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Risk";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Risk";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Risk";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Risk";
  } else {
    return "Unknown Risk";
  }
};

// ✅ Transform helpers
function transformRun1(run1) {
  return [
    {
      "Metric Name": "Vulnerability Count",
      "Metric Value": run1.vulnerabilityCount,
      Explanation: "True Positive Vulnerabilities",
    },
    {
      "Metric Name": "Vulnerability Density",
      "Metric Value": run1.vulnerabilityDensity,
      Explanation: "per 1,000 LoC",
    },
    {
      "Metric Name": "ROI",
      "Metric Value": run1.ROI,
      Explanation: "ROI Multiplier",
    },
    {
      "Metric Name": "Risk Rating",
      "Metric Value": run1.overallRiskRating,
      Explanation: getRiskWeightedDescription(run1.overallRiskRating),
    },
    {
      "Metric Name": "Vulnerability Distribution",
      "Metric Value": run1.vulnerabilityDistribution,
    },
    {
      "Metric Name": "False Positive Rate",
      "Metric Value": run1.falsePositiveRate,
      Explanation: "False Alarms",
    },
    {
      "Metric Name": "True Positive Rate",
      "Metric Value": run1.truePositiveRate,
      Explanation: "Actual Vulnerabilities",
    },
    {
      "Metric Name": "Business Impact Score",
      "Metric Value": run1.businessImpactScore,
      Explanation: getBusinessImpactDescription(run1.businessImpactScore),
    },
    {
      "Metric Name": "Technical Impact Score",
      "Metric Value": run1.technicalImpactScore,
      Explanation: getTechnicalImpactDescription(run1.technicalImpactScore),
    },
    {
      "Metric Name": "Compliance Status",
      Value: [{ Standard: "OWASP", Status: run1.complianceMeet }],
    },
  ];
}

function transformRun2(run2) {
  return [
    {
      "Metric Name": "Vulnerability Count",
      "Metric Value": run2.vulnerabilitiesCount,
      Explanation: "True Positive Vulnerabilities",
    },
    {
      "Metric Name": "Vulnerability Density",
      "Metric Value": run2.vulnerabilityDensity,
      Explanation: "per 1,000 LoC",
    },
    {
      "Metric Name": "ROI",
      "Metric Value": run2.ROI,
      Explanation: "ROI Multiplier",
    },
    {
      "Metric Name": "Risk Rating",
      "Metric Value": run2.overallRiskRating.toFixed(2),
      Explanation: getRiskWeightedDescription(run2.overallRiskRating),
    },
    {
      "Metric Name": "Business Impact Score",
      "Metric Value": run2.businessImpactScore,
      Explanation: getBusinessImpactDescription(run2.businessImpactScore),
    },
    {
      "Metric Name": "Technical Impact Score",
      "Metric Value": run2.technicalImpactScore,
      Explanation: getTechnicalImpactDescription(run2.technicalImpactScore),
    },
    {
      "Metric Name": "Security Posture",
      "Metric Value": run2.securityPosture,
      Explanation: getSecurityPostureDescription(run2.securityPosture),
    },
    {
      "Metric Name": "Vulnerability Distribution (R2)",
      "Metric Value": run2.openVulnerabilityDistribution,
    },
    {
      "Metric Name": "Closed Vulnerabilities by Severity",
      "Metric Value": run2.closedVulnerabilityDistribution,
    },
    {
      "Metric Name": "Age of Vulnerabilities (Days)",
      "Metric Value": run2.averageVulnerabiloityAge,
    },
    {
      "Metric Name": "Average Time to Remediate",
      "Metric Value": run2.averageRemediateDays,
    },
    {
      "Metric Name": "New Vulnerabilities in R2",
      Value: run2.newVulnerabilityDistribution,
    },
    {
      "Metric Name": "Compliance Requirements Met",
      Value: [{ Standard: "OWASP", Status: run2.complianceMeet }],
    },
  ];
}

const MetricCard = ({
  title,
  value,
  subtitle,
  isLarge = true,
  tooltipText = "", // Single prop for tooltip text
}) => {
  return (
    <Paper
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
        backgroundColor: "#f8f9fa",
        height: isLarge ? "180px" : "100px",
        borderRadius: "8px",
        border: "1px solid #5391F7",
        justifyContent: "center",
        position: "relative", // Added for absolute positioning of tooltip
      }}
    >
      {/* Info Icon with Tooltip */}
      {tooltipText && (
        <Tooltip
          title={tooltipText}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 300, // Limit tooltip width for better readability
                fontSize: "14px",
                lineHeight: 1.4,
              },
            },
          }}
        >
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              padding: "4px",
              color: "#5391F7",
              "&:hover": {
                backgroundColor: "rgba(83, 145, 247, 0.1)",
              },
            }}
            aria-label="Info"
          >
            <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Card Content */}
      <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "18px" }}>
        {title}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: "27px", mb: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: "15px", fontWeight: 500 }}>
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
};

const ComplianceCard = ({ title, status, tooltipText = "" }) => (
  <Paper
    sx={{
      p: 2,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      textAlign: "center",
      backgroundColor: "#f8f9fa",
      height: "180px", // Removed duplicate height property
      borderRadius: "8px",
      border: "1px solid #5391F7",
      position: "relative", // Added for tooltip positioning
    }}
  >
    {/* Info Icon with Tooltip */}
    {tooltipText && (
      <Tooltip
        title={tooltipText}
        placement="top"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 300,
              fontSize: "14px",
              lineHeight: 1.4,
            },
          },
        }}
      >
        <IconButton
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            padding: "4px",
            color: "#5391F7",
            "&:hover": {
              backgroundColor: "rgba(83, 145, 247, 0.1)",
            },
          }}
          aria-label="Info"
        >
          <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
        </IconButton>
      </Tooltip>
    )}

    {/* Card Content */}
    <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "18px" }}>
      {title}
    </Typography>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: "27px",
      }}
    >
      {status}
    </Typography>
  </Paper>
);

const run1TooltipTexts = {
  "Vulnerability Count":
    "Total number of code-level security flaws detected during the SAST scan. A lower value indicates better code hygiene.",
  "Vulnerability Density":
    "Number of vulnerabilities detected for every 1,000 lines of code. A lower value means cleaner, more secure code.",
  ROI: "Estimated value gained from remediation efforts compared to the cost of addressing vulnerabilities.",
  "Risk Rating":
    "Numeric score summarizing overall risk from detected SAST vulnerabilities. Lower values mean lower residual risk.",
  "Overall Risk Rating":
    "Numeric score summarizing overall risk from detected SAST vulnerabilities. Lower values mean lower residual risk.",
  "Vulnerability Distribution by Severity":
    "Proportion of identified vulnerabilities categorized as Critical, High, Medium, or Low severity based on SAST results.",
  "False Positive Rate":
    "Percentage of reported issues that are incorrectly flagged as vulnerabilities. Lower rates indicate more accurate SAST scanning.",
  "True Positive Rate":
    "Percentage of correctly identified vulnerabilities in the total findings. Higher rates mean more reliable detection.",
  "Business Impact Score":
    "Degree to which identified code flaws could disrupt business operations if exploited. Higher scores signal greater business risk.",
  "Technical Impact Score":
    "Extent to which vulnerabilities affect the system's technical performance, stability, or architecture.",
  "Return on Investment":
    "Estimated value gained from remediation efforts compared to the cost of addressing vulnerabilities.",
  "Compliance Met":
    "Indicates how well the codebase meets relevant compliance and regulatory security standards.",
  "Security Posture":
    "Represents the percentage of the application’s security resilience based on identified static code vulnerabilities. A lower value indicates stronger code quality, reduced vulnerabilities, and better adherence to secure coding practices.",
  "Closed Vulnerabilities":
    "Count of vulnerabilities fixed since Run 1, categorized by severity.",
  "New Vulnerabilities":
    "Number of vulnerabilities that were not present in Run 1 but detected in Run 2.",
  "Age of vulnerability":
    "Average time vulnerabilities in the current scan have been present in the codebase.",
  "Average Time To Remediate":
    "Mean time taken to fix vulnerabilities identified in SAST scans.",
  "OWASP Top 10":
    "Indicates whether all OWASP Top 10 vulnerability categories were identified and addressed. Useful for assessing security coverage.",
};

const SecurityMetrics = ({ run1Metrics, run2Metrics }) => {
  // 🔹 Transform raw API objects into UI array format
  const Run1Metrics = run1Metrics ? transformRun1(run1Metrics) : [];
  const Run2Metrics = run2Metrics ? transformRun2(run2Metrics) : [];

  const run1OWASPData = transformOWASPData(run1Metrics.OWASPMetrics);
  const run1TotalVulnerabilities = run1Metrics.vulnerabilityCount;

  const run2OWASPData = transformOWASPData(run2Metrics.owaspResponse);
  const run2TotalVulnerabilities = run2Metrics.vulnerabilitiesCount;

  // === Your original JSX + logic ===
  const selectedRun1Metrics1 = [
    "Vulnerability Count",
    "Vulnerability Density",
    "ROI",
    "Risk Rating",
  ];
  const displayedRun1Metrics1 = Run1Metrics.filter((metric) =>
    selectedRun1Metrics1.includes(metric["Metric Name"])
  );
  const formatRun1MetricValue1 = (metric) => {
    switch (metric["Metric Name"]) {
      case "ROI":
        return `${metric["Metric Value"].toFixed(2)}`;
      case "Risk Rating":
        return `${metric["Metric Value"]}/10`;
      case "Vulnerability Density":
        return metric["Metric Value"].toFixed(2);
      default:
        return metric["Metric Value"];
    }
  };

  const severityColors = {
    Critical: "#c00000",
    High: "#ff0000",
    Medium: "#ffc000",
    Low: "#39b549",
  };
  const vulnerabilityDistributionRun1Metric = Run1Metrics.find(
    (m) => m["Metric Name"] === "Vulnerability Distribution"
  );
  const run1PieData = Object.entries(
    vulnerabilityDistributionRun1Metric["Metric Value"]
  ).map(([severity, value]) => ({
    name: severity,
    value,
    color: severityColors[severity] || "#000000",
  }));

  const selectedRun1Metrics2 = [
    "False Positive Rate",
    "True Positive Rate",
    "Business Impact Score",
    "Technical Impact Score",
  ];
  const displayedRun1Metrics2 = Run1Metrics.filter((metric) =>
    selectedRun1Metrics2.includes(metric["Metric Name"])
  );
  const formatRun1MetricValue2 = (metric) => {
    switch (metric["Metric Name"]) {
      case "False Positive Rate":
      case "True Positive Rate":
        return `${metric["Metric Value"]}%`;
      case "Business Impact Score":
      case "Technical Impact Score":
        return `${metric["Metric Value"]}/10`;
      default:
        return metric["Metric Value"];
    }
  };

  const selectedRun2Metrics1 = [
    "Vulnerability Count",
    "Vulnerability Density",
    "ROI",
    "Risk Rating",
    "Security Posture",
  ];
  const filteredRun2Metrics1 = Run2Metrics.filter((metric) =>
    selectedRun2Metrics1.includes(metric["Metric Name"])
  );
  const displayedRun2Metrics1 = filteredRun2Metrics1.sort(
    (a, b) =>
      selectedRun2Metrics1.indexOf(a["Metric Name"]) -
      selectedRun2Metrics1.indexOf(b["Metric Name"])
  );
  const formatRun2MetricValue1 = (metric) => {
    switch (metric["Metric Name"]) {
      case "ROI":
        return `${metric["Metric Value"]}`;
      case "Risk Rating":
        return `${metric["Metric Value"]}/10`;
      case "Security Posture":
        return `${metric["Metric Value"]}/10`;
      default:
        return metric["Metric Value"];
    }
  };

  const selectedRun2Metrics2 = [
    "Business Impact Score",
    "Technical Impact Score",
    // "Age of Vulnerabilities (Days)",
    // "Average Time to Remediate",
  ];
  const displayedRun2Metrics2 = Run2Metrics.filter((metric) =>
    selectedRun2Metrics2.includes(metric["Metric Name"])
  );
  const formatRun2MetricValue2 = (metric) => {
    switch (metric["Metric Name"]) {
      case "Business Impact Score":
      case "Technical Impact Score":
        return `${metric["Metric Value"].toFixed(2)}/10`;
      case "Average Time to Remediate":
      case "Age of Vulnerabilities (Days)":
        return `${metric["Metric Value"]} days`;
      default:
        return metric["Metric Value"];
    }
  };

  const vulnerabilityDistributionRun2Metric = Run2Metrics.find(
    (m) => m["Metric Name"] === "Vulnerability Distribution (R2)"
  );
  const run2PieData = Object.entries(
    vulnerabilityDistributionRun2Metric["Metric Value"]
  ).map(([severity, value]) => ({
    name: severity,
    value,
    color: severityColors[severity] || "#000000",
  }));

  const closedVulnerabilityDistributionRun2Metric = Run2Metrics.find(
    (m) => m["Metric Name"] === "Closed Vulnerabilities by Severity"
  );
  const run2ClosedPieData = Object.entries(
    closedVulnerabilityDistributionRun2Metric["Metric Value"]
  ).map(([severity, value]) => ({
    name: severity,
    value,
    color: severityColors[severity] || "#000000",
  }));

  const complianceMetric = Run1Metrics.find(
    (m) => m["Metric Name"] === "Compliance Status"
  );
  const complianceList = complianceMetric?.["Value"] || [];
  const complianceMetric2 = Run2Metrics.find(
    (m) => m["Metric Name"] === "Compliance Requirements Met"
  );
  const complianceList2 = complianceMetric2?.["Value"] || [];

  const newVulnerabilities = Run2Metrics.find(
    (m) => m["Metric Name"] === "New Vulnerabilities in R2"
  );
  const newVulnerabilitiesObj = newVulnerabilities?.Value || {};
  const vulnerabilityData = Object.entries(newVulnerabilitiesObj).map(
    ([label, count]) => ({
      label,
      count,
      color: severityColors[label] || "#000000",
    })
  );

  const getMetricValue = (metrics, name) => {
    const metric = metrics.find((m) => m["Metric Name"] === name);
    return metric ? metric["Metric Value"] : null;
  };
  const summaryMetrics = [
    {
      title: "Vulnerability Count",
      run1: getMetricValue(Run1Metrics, "Vulnerability Count"),
      run2: getMetricValue(Run2Metrics, "Vulnerability Count"),
      subtitle: "True Positive Vulnerabilities",
    },
    {
      title: "Vulnerability Density",
      run1: getMetricValue(Run1Metrics, "Vulnerability Density").toFixed(2),
      run2: getMetricValue(Run2Metrics, "Vulnerability Density").toFixed(2),
      subtitle: "per 1,000 LoC",
    },
    {
      title: "Return on Investment",
      run1: getMetricValue(Run1Metrics, "ROI").toFixed(2),
      run2: getMetricValue(Run2Metrics, "ROI").toFixed(2),
      subtitle: "ROI per dollar",
    },
    {
      title: "Risk Rating",
      run1: getMetricValue(Run1Metrics, "Risk Rating"),
      run2: getMetricValue(Run2Metrics, "Risk Rating"),
      subtitle: "% Risk Improvement",
    },
  ];

  // === Full original JSX ===
  return (
    <Box sx={{ p: 3, backgroundColor: "#FFFFFF", minHeight: "100vh" }}>
      {/* Top Heading */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "34px" }}>
          Security Metrics Analysis
        </Typography>
        {/* <Button
          sx={{
            backgroundColor: "#0148A2",
            color: "white",
            width: "209px",
            height: "40px",
            textTransform: "none",
            fontSize: "14px",
          }}
        >
          Run1 vs Run2 Comparison
        </Button> */}
      </Box>

      {/* Run 1 - Baseline Security Metrics */}
      <Box sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 2, fontSize: "34px" }}>
            Run 1 - Baseline Security Metrics
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {displayedRun1Metrics1.map((metric) => (
              <Grid item xs={3} key={metric["Metric Name"]}>
                <MetricCard
                  title={metric["Metric Name"]}
                  value={formatRun1MetricValue1(metric)}
                  subtitle={metric.Explanation}
                  tooltipText={run1TooltipTexts[metric["Metric Name"]] || ""}
                />
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              mt: 4,
              border: "1px solid #5391F7",
              borderRadius: "8px",
              p: 3,
              position: "relative", // Added for tooltip positioning
            }}
          >
            {/* Info Icon with Tooltip - Top Right Corner */}
            <Tooltip
              title={run1TooltipTexts["Vulnerability Distribution by Severity"]}
              placement="top"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "14px",
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>

            <Typography
              sx={{ fontWeight: 600, mt: 4, mb: 2, fontSize: "23px" }}
            >
              Run 1 - Vulnerability Distribution by Severity
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Box sx={{ width: "200px", height: "200px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={run1PieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      dataKey="value"
                    >
                      {run1PieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {run1PieData.map((item) => (
                  <Box
                    key={item.name}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: item.color,
                        borderRadius: "10px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 400,
                        minWidth: "60px",
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Run 1 - Compliance Requirements */}
      <Box sx={{ mb: 3 }}>
        {/* <Typography sx={{ fontWeight: 700, mb: 2, fontSize: "34px" }}>
          Run 1 - Compliance Requirements Assessment
        </Typography> */}

        <Grid container spacing={2}>
          {displayedRun1Metrics2.map((metric) => (
            <Grid item xs={12} sm={6} md={3} key={metric["Metric Name"]}>
              <MetricCard
                title={metric["Metric Name"]}
                value={formatRun1MetricValue2(metric)}
                subtitle={metric.Explanation}
                tooltipText={run1TooltipTexts[metric["Metric Name"]] || ""}
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          {complianceList.map((compliance, idx) => (
            <Grid item xs={3} key={idx}>
              <ComplianceCard
                title={compliance.Standard}
                status={compliance.Status}
                tooltipText={run1TooltipTexts["Compliance Met"] || ""}
              />
            </Grid>
          ))}
        </Grid>

        {/* OWASP Top 10 Distribution */}
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mt: { xs: 2 }, mb: { xs: 4, md: 6 } }}
        >
          <Grid item xs={12}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "#5391F7",
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                height: "100%",
                position: "relative",
              }}
            >
              <Tooltip
                title={run1TooltipTexts["OWASP Top 10"]}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 16, // Increased from 8 to give more space from border
                    right: 16, // Increased from 8 to give more space from border
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>
              <Typography sx={{ fontSize: "23px", fontWeight: 400, mb: 2 }}>
                Vulnerability Distribution by OWASP Top 10
              </Typography>

              {run1OWASPData.length > 0 ? (
                <Stack spacing={2}>
                  {run1OWASPData.map((item, i) => (
                    <ProgressBarMUI
                      key={i}
                      label={item.label}
                      value={item.value}
                      total={run1TotalVulnerabilities}
                      color={item.color}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  No OWASP categorization data available
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Run 2 - Current Security Metrics */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "34px", fontWeight: 600, mb: 2 }}>
          Run 2 - Current Security Metrics
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {displayedRun2Metrics1.map((metric) => (
            <Grid item xs={3} key={metric["Metric Name"]}>
              <MetricCard
                title={metric["Metric Name"]}
                value={formatRun2MetricValue1(metric)}
                subtitle={metric.Explanation}
                tooltipText={run1TooltipTexts[metric["Metric Name"]] || ""}
              />
            </Grid>
          ))}
          {complianceList2.map((c, idx) => (
            <Grid item xs={3} key={idx}>
              <ComplianceCard
                title={c.Standard}
                status={c.Status}
                tooltipText={run1TooltipTexts["Compliance Met"] || ""}
              />
            </Grid>
          ))}

          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: "100%",
                border: "1px solid #5391F7",
                borderRadius: "8px",
                position: "relative", // Added for tooltip positioning
              }}
            >
              {/* Info Icon with Tooltip */}
              <Tooltip
                title={run1TooltipTexts["New Vulnerabilities"]}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                    zIndex: 1, // Ensure it's above card content
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>

              <CardContent>
                <Typography sx={{ mb: 1, fontSize: "18px", fontWeight: 600 }}>
                  New Vulnerabilities in Run 2
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    p: 1,
                  }}
                >
                  {vulnerabilityData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: "50%",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "14px", fontWeight: 500 }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "14px", fontWeight: 500 }}
                      >
                        {item.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {/* Open Vulnerabilities */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
                gap: 2,
                mt: 4,
                border: "1px solid #5391F7",
                borderRadius: "8px",
                position: "relative", // tooltip positioning
              }}
            >
              {/* Info Icon with Tooltip */}
              <Tooltip
                title={
                  run1TooltipTexts["Vulnerability Distribution by Severity"]
                }
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: { maxWidth: 300, fontSize: "14px", lineHeight: 1.4 },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>

              <Typography
                sx={{ fontWeight: 600, mt: 4, mb: 2, fontSize: "23px" }}
              >
                Run 2 - Open Vulnerabilities by Severity
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Box sx={{ width: "200px", height: "200px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={run2PieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {run2PieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {run2PieData.map((item) => (
                    <Box
                      key={item.name}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: "10px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          minWidth: "60px",
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Closed Vulnerabilities */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
                gap: 2,
                mt: 4,
                border: "1px solid #5391F7",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              <Tooltip
                title={run1TooltipTexts["Closed Vulnerabilities"]}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: { maxWidth: 300, fontSize: "14px", lineHeight: 1.4 },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": { backgroundColor: "rgba(83, 145, 247, 0.1)" },
                  }}
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>

              <Typography
                sx={{ fontWeight: 600, mt: 4, mb: 2, fontSize: "23px" }}
              >
                Run 2 - Closed Vulnerabilities by Severity
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Box sx={{ width: "200px", height: "200px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={run2ClosedPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {run2ClosedPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {run2ClosedPieData.map((item) => (
                    <Box
                      key={item.name}
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: "10px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          minWidth: "60px",
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Run 2 - Compliance */}
      <Grid sx={{ mb: 2 }}>
        {/* <CardContent> */}
        {/* <Typography sx={{ fontSize: "34px", fontWeight: 600, mb: 2 }}>
            Run 2 - Compliance Requirements Assessment
          </Typography> */}

        <Grid container spacing={2} sx={{ mt: 2 }}>
          {displayedRun2Metrics2.map((metric) => (
            <Grid item xs={6} key={metric["Metric Name"]}>
              <MetricCard
                title={metric["Metric Name"]}
                value={formatRun2MetricValue2(metric)}
                subtitle={metric.Explanation}
                tooltipText={run1TooltipTexts[metric["Metric Name"]] || ""}
              />
            </Grid>
          ))}
        </Grid>
        {/* </CardContent> */}

        {/* OWASP Top 10 Distribution */}
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mt: { xs: 2 }, mb: { xs: 4, md: 6 } }}
        >
          <Grid item xs={12}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "#5391F7",
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                height: "100%",
                position: "relative",
              }}
            >
              <Tooltip
                title={run1TooltipTexts["OWASP Top 10"]}
                placement="top"
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: "14px",
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 16, // Increased from 8 to give more space from border
                    right: 16, // Increased from 8 to give more space from border
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>
              <Typography sx={{ fontSize: "23px", fontWeight: 400, mb: 2 }}>
                Vulnerability Distribution by OWASP Top 10
              </Typography>

              {run2OWASPData.length > 0 ? (
                <Stack spacing={2}>
                  {run2OWASPData.map((item, i) => (
                    <ProgressBarMUI
                      key={i}
                      label={item.label}
                      value={item.value}
                      total={run2TotalVulnerabilities}
                      color={item.color}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  No OWASP categorization data available
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* Summary */}
      <Grid>
        <CardContent>
          <Typography sx={{ fontSize: "34px", fontWeight: 600, mb: 2 }}>
            Summary
          </Typography>
          <Grid container spacing={2}>
            {summaryMetrics.map((metric) => (
              <Grid item xs={3} key={metric.title}>
                <MetricCard
                  title={metric.title}
                  value={`${metric.run1} → ${metric.run2}`}
                  subtitle={metric.subtitle}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Grid>
    </Box>
  );
};

export default SecurityMetrics;
